import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { DatasetListResponse } from "datasets/types/interfaces/datasetListItem";
import { Trash2, X } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { ChipsMultiSelect } from "~/components/ui/chips-multi-select";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { MarkdownEditor } from "~/components/ui/markdown-editor";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "~/components/ui/sheet";
import { Switch } from "~/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { Textarea } from "~/components/ui/textarea";
import useToast from "~/components/ui/toast";
import { useAgentList } from "~/hooks/useAgentList";
import { useFetcher } from "~/hooks/useFetcher";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { DatasetIcon } from "~/pages/manifold-nextgen/components/datasets/DatasetIcon";
import { AgentListView, AgentStatus } from "~/services/agents";
import { catalogDatasetsListUrl } from "~/services/catalog";
import {
  deleteQuickAction,
  QuickAction,
  QuickActionPlacement,
  updateQuickAction,
} from "~/services/quickActions";
import { IconPicker } from "./IconPicker";
import { PLACEMENT_ICON, PLACEMENT_LABEL, resolvePlacementTargetName } from "./constants";

interface QuickActionDrawerProps {
  quickAction: QuickAction;
  open: boolean;
  onClose: () => void;
  onSaved: () => unknown;
  onDeleted: () => unknown;
}

interface DrawerFormData {
  icon: string;
  title: string;
  subtitle: string;
  enabled: boolean;
  projectName: string;
  projectDescription: string;
  seedPrompt: string;
  agentIds: string[];
  datasetIds: string[];
}

// Matches the read-only agent/dataset preview pills on the project setup
// wizard (PreviewRow), plus a remove control since these chips are editable.
const CHIP_CLASSNAME =
  "inline-flex items-center gap-1.5 rounded-full border border-border bg-background py-0.5 pr-1 pl-1.5";

const toFormData = (quickAction: QuickAction): DrawerFormData => ({
  icon: quickAction.icon ?? "",
  title: quickAction.title,
  subtitle: quickAction.subtitle ?? "",
  enabled: quickAction.enabled,
  projectName: quickAction.project_name,
  projectDescription: quickAction.project_description ?? "",
  seedPrompt: quickAction.seed_prompt ?? "",
  agentIds: quickAction.agents.map((a) => a.agent_id),
  datasetIds: quickAction.datasets.map((d) => d.dataset_id),
});

export const QuickActionDrawer = ({
  quickAction,
  open,
  onClose,
  onSaved,
  onDeleted,
}: QuickActionDrawerProps) => {
  const { showSuccess, showError } = useToast();
  const [activeTab, setActiveTab] = useState<"details" | "project">("details");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data: agentList } = useAgentList(open, AgentListView.USABLE);
  const { data: datasetList } = useFetcher<DatasetListResponse>(
    open ? catalogDatasetsListUrl : null
  );
  const datasets = datasetList?.items;
  const enabledAgents = (agentList?.items ?? []).filter(
    (agent) => agent.status !== AgentStatus.DISABLED
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, dirtyFields },
    setValue,
    watch,
    reset,
  } = useForm<DrawerFormData>({ mode: "onChange", defaultValues: toFormData(quickAction) });

  useEffect(() => {
    if (open) {
      reset(toFormData(quickAction));
      setActiveTab("details");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, quickAction.id]);

  const icon = watch("icon");
  const enabled = watch("enabled");
  const agentIds = watch("agentIds");
  const datasetIds = watch("datasetIds");
  const projectDescription = watch("projectDescription");

  const PlacementIcon = PLACEMENT_ICON[quickAction.placement];
  const agentNamesById = useMemo(
    () => new Map((agentList?.items ?? []).map((agent) => [agent.id, agent.display_name])),
    [agentList]
  );
  const datasetNamesById = useMemo(
    () => new Map((datasets ?? []).map((dataset) => [dataset.id, dataset.title])),
    [datasets]
  );
  const placementTargetName = resolvePlacementTargetName(
    quickAction.placement,
    quickAction.placement_id,
    agentNamesById,
    datasetNamesById
  );
  const placementAgent =
    quickAction.placement === QuickActionPlacement.AGENT && quickAction.placement_id
      ? (agentList?.items ?? []).find((a) => a.id === quickAction.placement_id)
      : undefined;
  const placementDataset =
    quickAction.placement === QuickActionPlacement.DATASET && quickAction.placement_id
      ? (datasets ?? []).find((d) => d.id === quickAction.placement_id)
      : undefined;

  const handleClose = () => {
    reset(toFormData(quickAction));
    onClose();
  };

  const onSubmit = async (data: DrawerFormData) => {
    setIsSubmitting(true);
    const payload: Parameters<typeof updateQuickAction>[1] = {
      ...(dirtyFields.title && { title: data.title.trim() }),
      ...(dirtyFields.subtitle && { subtitle: data.subtitle.trim() || null }),
      ...(dirtyFields.icon && { icon: data.icon || null }),
      ...(dirtyFields.enabled && { enabled: data.enabled }),
      ...(dirtyFields.projectName && { project_name: data.projectName.trim() }),
      ...(dirtyFields.projectDescription && {
        project_description: data.projectDescription.trim() || null,
      }),
      ...(dirtyFields.seedPrompt && { seed_prompt: data.seedPrompt.trim() || null }),
      ...(dirtyFields.agentIds && { agent_ids: data.agentIds }),
      ...(dirtyFields.datasetIds && { dataset_ids: data.datasetIds }),
    };

    try {
      await updateQuickAction(quickAction.id, payload);
      showSuccess("Quick action updated");
      await onSaved();
      onClose();
    } catch {
      showError("Failed to update quick action, please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteQuickAction(quickAction.id);
      showSuccess("Quick action deleted");
      setShowDeleteDialog(false);
      await onDeleted();
      onClose();
    } catch {
      showError("Failed to delete quick action, please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={(next) => !next && handleClose()}>
        <SheetContent
          className="flex w-[560px] flex-col gap-0 sm:max-w-[560px]"
          aria-describedby={undefined}
          onOpenAutoFocus={(e) => {
            // Prevent Radix from auto-focusing the Enabled switch (the first
            // focusable element) when the drawer opens.
            e.preventDefault();
          }}
        >
          <SheetHeader className="gap-1 px-6 pb-0">
            <SheetTitle className="text-2xl">{quickAction.title}</SheetTitle>
            <p className="text-xs text-muted-foreground">
              Created {new Date(quickAction.created_at).toLocaleDateString()}
            </p>
          </SheetHeader>

          <div className="flex items-start justify-between gap-4 px-6 py-4">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground">Enabled</span>
                <Switch
                  checked={enabled}
                  onCheckedChange={(value) => setValue("enabled", value, { shouldDirty: true })}
                  aria-label="Enabled"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {enabled
                  ? "This quick action is visible to users"
                  : "This quick action is hidden from users"}
              </p>
            </div>

            <div className="min-w-0 space-y-1.5 text-right">
              <Label>Placement</Label>
              <div className="flex min-w-0 items-center justify-end gap-1.5 text-sm">
                {placementTargetName ? (
                  <>
                    {placementAgent && <AgentAvatar agent={placementAgent} size={16} />}
                    {placementDataset && <DatasetIcon dataset={placementDataset} size={16} />}
                    <span className="truncate text-foreground">{placementTargetName}</span>
                  </>
                ) : (
                  <>
                    <PlacementIcon className="size-4 shrink-0 text-black dark:text-white" />
                    <span>{PLACEMENT_LABEL[quickAction.placement]}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-1 flex-col overflow-hidden">
            <Tabs
              value={activeTab}
              onValueChange={(value) => setActiveTab(value as "details" | "project")}
              className="flex flex-1 flex-col overflow-hidden"
            >
              <div className="px-6 pt-4 pb-2">
                <TabsList>
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="project">Configuration</TabsTrigger>
                </TabsList>
              </div>

              <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-1 flex-col overflow-hidden"
              >
                <div className="flex-1 overflow-y-auto px-6 pt-0 pb-4">
                  <TabsContent value="details" className="mt-0 space-y-4">
                    <div className="flex gap-4">
                      <div className="space-y-1.5">
                        <Label>Icon</Label>
                        <IconPicker
                          value={icon}
                          onChange={(value) => setValue("icon", value, { shouldDirty: true })}
                        />
                      </div>
                      <div className="flex-1 space-y-3">
                        <div className="space-y-1.5">
                          <Label htmlFor="qa-drawer-title">Title</Label>
                          <Input
                            id="qa-drawer-title"
                            {...register("title", { required: "Title is required" })}
                          />
                          {errors.title && (
                            <p className="text-sm text-destructive">{errors.title.message}</p>
                          )}
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="qa-drawer-subtitle">Subtitle</Label>
                          <Input id="qa-drawer-subtitle" {...register("subtitle")} />
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="project" className="mt-0 space-y-4">
                    <p className="text-sm text-muted-foreground">
                      A new project will be created with the following configuration when a user
                      triggers this quick action.
                    </p>
                    <div className="space-y-1.5">
                      <Label htmlFor="qa-drawer-project-name">Project name</Label>
                      <Input
                        id="qa-drawer-project-name"
                        {...register("projectName", { required: "Project name is required" })}
                      />
                      {errors.projectName && (
                        <p className="text-sm text-destructive">{errors.projectName.message}</p>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="qa-drawer-project-description">Project description</Label>
                      <MarkdownEditor
                        id="qa-drawer-project-description"
                        value={projectDescription}
                        onChange={(value) =>
                          setValue("projectDescription", value, { shouldDirty: true })
                        }
                        paneClassName="min-h-20 max-h-48 overflow-y-auto"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="qa-drawer-seed-prompt">Seed prompt</Label>
                      <Textarea id="qa-drawer-seed-prompt" rows={4} {...register("seedPrompt")} />
                    </div>

                    <div className="space-y-1.5 border-t pt-4">
                      <ChipsMultiSelect
                        label="Datasets"
                        placeholder="Add datasets..."
                        secondaryPlaceholder="Add more datasets..."
                        searchPlaceholder="Search datasets to add"
                        emptyText="No datasets found."
                        selectedValues={datasetIds}
                        options={(datasets ?? []).map((dataset) => ({
                          value: dataset.id,
                          label: dataset.title,
                          icon: <DatasetIcon dataset={dataset} size={16} />,
                        }))}
                        onChange={(values) => setValue("datasetIds", values, { shouldDirty: true })}
                        renderChip={({ value, option, handleRemove }) => (
                          <span key={value} className={CHIP_CLASSNAME}>
                            <DatasetIcon
                              dataset={(datasets ?? []).find((d) => d.id === value)}
                              size={18}
                            />
                            <span className="max-w-[9rem] truncate text-xs text-foreground">
                              {option?.label ?? value}
                            </span>
                            <span
                              role="button"
                              tabIndex={0}
                              className="group flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-muted"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemove(value);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleRemove(value);
                                }
                              }}
                              aria-label={`Remove ${option?.label}`}
                            >
                              <X className="size-3 text-muted-foreground" />
                            </span>
                          </span>
                        )}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <ChipsMultiSelect
                        label="Agents"
                        placeholder="Add agents..."
                        secondaryPlaceholder="Add more agents..."
                        searchPlaceholder="Search agents to add"
                        emptyText="No agents found."
                        selectedValues={agentIds}
                        options={enabledAgents.map((agent) => ({
                          value: agent.id,
                          label: agent.display_name,
                          icon: <AgentAvatar agent={agent} size={16} />,
                        }))}
                        onChange={(values) => setValue("agentIds", values, { shouldDirty: true })}
                        renderChip={({ value, option, handleRemove }) => (
                          <span key={value} className={CHIP_CLASSNAME}>
                            <AgentAvatar
                              agent={(agentList?.items ?? []).find((a) => a.id === value)}
                              size={18}
                            />
                            <span className="max-w-[9rem] truncate text-xs text-foreground">
                              {option?.label ?? value}
                            </span>
                            <span
                              role="button"
                              tabIndex={0}
                              className="group flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-muted"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemove(value);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleRemove(value);
                                }
                              }}
                              aria-label={`Remove ${option?.label}`}
                            >
                              <X className="size-3 text-muted-foreground" />
                            </span>
                          </span>
                        )}
                      />
                    </div>
                  </TabsContent>
                </div>

                <div className="flex items-center justify-between gap-2 border-t px-6 py-4">
                  <Button
                    type="button"
                    variant="outline"
                    className="text-destructive hover:text-destructive dark:text-destructive dark:hover:text-destructive"
                    onClick={() => setShowDeleteDialog(true)}
                  >
                    <Trash2 className="size-4" />
                    Delete
                  </Button>
                  <div className="flex items-center gap-2">
                    {isDirty && (
                      <Button type="button" variant="outline" onClick={handleClose}>
                        Cancel
                      </Button>
                    )}
                    <Button type="submit" disabled={!isDirty || isSubmitting}>
                      {isSubmitting ? "Saving…" : "Save"}
                    </Button>
                  </div>
                </div>
              </form>
            </Tabs>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete quick action?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove &quot;{quickAction.title}&quot;. This can&apos;t be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting…" : "Delete Quick Action"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
